import React, { useState, useEffect } from 'react';
import { CommonButton } from '@/components/ui/common-button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import rakeMovementService from '@/services/rakeMovementService';
import userService from '@/services/userService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const ViewMissingWagonDetailsDialog = ({ open, onOpenChange, trip }) => {
  const [existingReceipts, setExistingReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  const movementId = trip?.id || trip?.rake_movement_id;

  useEffect(() => {
    if (open && movementId) {
      fetchExistingReceipts(movementId);
    }
  }, [open, movementId]);

  const fetchExistingReceipts = async (rakeMovementId) => {
    try {
      setLoadingReceipts(true);
      const data = await rakeMovementService.getMissingWagonReceipts(rakeMovementId);
      const list = Array.isArray(data) ? data : data?.data || [];
      setExistingReceipts(list);

      const userIdsToFetch = [...new Set(
        list
          .filter(item => !item.created_by_username && item.created_by)
          .map(item => item.created_by)
      )];

      if (userIdsToFetch.length > 0) {
        const newMap = { ...usersMap };
        await Promise.all(
          userIdsToFetch.map(async (userId) => {
            if (!newMap[userId]) {
              try {
                const userObj = await userService.getUserById(userId);
                newMap[userId] = userObj?.username || userObj?.name || userObj?.email || userId;
              } catch (e) {
                newMap[userId] = userId.length > 8 ? `${userId.slice(0, 8)}...` : userId;
              }
            }
          })
        );
        setUsersMap(newMap);
      }
    } catch (err) {
      console.error('Failed to load missing wagon receipts:', err);
      setExistingReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const getRecordedByText = (item) => {
    if (item.created_by_username) return item.created_by_username;
    if (item.created_by) {
      return usersMap[item.created_by] || (item.created_by.length > 8 ? `${item.created_by.slice(0, 8)}...` : item.created_by);
    }
    return '—';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">
            Missing Wagon Receipt Details
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-5">
          <div className="border border-border p-3.5 rounded-lg bg-card grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground block text-xs font-semibold">Trip Number</span>
              <span className="font-semibold text-foreground">{trip?.trip_number || 'N/A'}</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-xs font-semibold">RR Number</span>
              <span className="font-semibold text-foreground">{trip?.rr_number || 'N/A'}</span>
            </div>
            <div className="pt-2 border-t">
              <span className="text-muted-foreground block text-xs font-semibold">Origin</span>
              <span className="font-semibold text-foreground text-xs">{trip?.rake_point_name || trip?.rake_point?.name || 'N/A'}</span>
            </div>
            <div className="pt-2 border-t">
              <span className="text-muted-foreground block text-xs font-semibold">Baseline Qty</span>
              <span className="font-bold text-primary">{trip?.baseline_qty_mt ? `${parseFloat(trip.baseline_qty_mt).toFixed(2)} MT` : '0.00 MT'}</span>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold text-foreground mb-2">Received Missing Wagon Receipts</h4>
            {loadingReceipts ? (
              <div className="text-xs text-muted-foreground py-4 text-center">Loading receipt details...</div>
            ) : existingReceipts.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-8 text-xs py-1">Received Wagons</TableHead>
                      <TableHead className="h-8 text-xs py-1">Receiving Date</TableHead>
                      <TableHead className="h-8 text-xs py-1">Recorded By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {existingReceipts.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="py-1.5 text-xs font-semibold">{parseInt(item.qty, 10)}</TableCell>
                        <TableCell className="py-1.5 text-xs">{formatDate(item.receiving_date)}</TableCell>
                        <TableCell className="py-1.5 text-xs font-medium">{getRecordedByText(item)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground py-4 px-4 border border-dashed rounded-md text-center bg-muted/20">
                No missing wagon receipts recorded for this rake movement yet.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <CommonButton type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </CommonButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewMissingWagonDetailsDialog;
